import { OktaAuth } from "@okta/okta-auth-js";
import oktaAuth from "./oktaAuth";
import { loginWithOfficeDialog, isOfficeDialogAvailable } from "./authDialog";
import { getOktaConfigForEnvironment } from "./oktaEnvironments";
import { clearSelectedEnvironment } from "../services/environmentStorage";
import { getSensitiveItem } from "../services/storageService";
import tokenManager from "../services/tokenManager";
import logger from "../../logger";

export async function persistTokens(tokens) {
  return tokenManager.persistTokens(tokens);
}

export async function restoreTokens() {
  return tokenManager.restoreTokens();
}

const clearAllCookies = async () => {
  try {
    const issuer = oktaAuth.options.issuer;
    const oktaDomain = issuer ? new URL(issuer).hostname : null;

    const currentDomain = window.location.hostname;
    const domainParts = currentDomain.split(".");

    const domainsToClear = [
      currentDomain,
      `.${currentDomain}`,
      domainParts.length > 1 ? `.${domainParts.slice(-2).join(".")}` : null,
      oktaDomain,
      oktaDomain ? `.${oktaDomain}` : null,
    ].filter(Boolean);

    const cookies = document.cookie.split(";");

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      domainsToClear.forEach((dom) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${dom}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${dom};SameSite=None;Secure`;
      });
    });

    const oktaCookieNames = [
      "okta-oauth-state",
      "okta-oauth-nonce",
      "okta-oauth-pkce",
      "okta-oauth-redirect-uri",
      "okta-session",
      "sid",
      "JSESSIONID",
      "DT",
      "proximity",
    ];

    oktaCookieNames.forEach((cookieName) => {
      domainsToClear.forEach((dom) => {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${dom}`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${dom};SameSite=None;Secure`;
      });
    });

    logger.debug("Cookies cleared");
  } catch (err) {
    logger.warn("Error clearing cookies:", err);
  }
};

export async function clearStoredTokens() {
  try {
    await tokenManager.clearTokens();
    await clearSelectedEnvironment();
    logger.debug("All auth storage cleared");
  } catch (err) {
    logger.warn("Error clearing storage:", err);
  }
}

export const loginWithPopup = async (environment) => {
  if (!environment) {
    throw new Error("Environment must be specified");
  }

  if (isOfficeDialogAvailable()) {
    return loginWithOfficeDialog(environment)
      .then(async (result) => {
        logger.debug("Loading tokens from storage after dialog");
        const tokensLoaded = await loadTokensFromStorage();

        if (tokensLoaded) {
          logger.debug("Tokens loaded from storage");
        } else {
          if (result.tokens) {
            try {
              const tokenObjects = {};

              if (result.tokens.accessToken) {
                let accessTokenValue = null;
                if (result.tokens.accessToken.value) {
                  accessTokenValue = result.tokens.accessToken.value;
                } else if (typeof result.tokens.accessToken === "string") {
                  accessTokenValue = result.tokens.accessToken;
                } else if (result.tokens.accessToken.accessToken) {
                  accessTokenValue = result.tokens.accessToken.accessToken;
                }

                if (accessTokenValue) {
                  tokenObjects.accessToken = {
                    accessToken: accessTokenValue,
                    expiresAt: result.tokens.accessToken.expiresAt,
                    tokenType: result.tokens.accessToken.tokenType || "Bearer",
                    scopes: result.tokens.accessToken.scopes || [],
                  };
                }
              }

              if (result.tokens.idToken) {
                let idTokenValue = null;
                if (result.tokens.idToken.value) {
                  idTokenValue = result.tokens.idToken.value;
                } else if (typeof result.tokens.idToken === "string") {
                  idTokenValue = result.tokens.idToken;
                } else if (result.tokens.idToken.idToken) {
                  idTokenValue = result.tokens.idToken.idToken;
                }

                if (idTokenValue) {
                  tokenObjects.idToken = {
                    idToken: idTokenValue,
                    expiresAt: result.tokens.idToken.expiresAt,
                    claims: result.tokens.idToken.claims || {},
                  };
                }
              }

              if (result.tokens.refreshToken) {
                let refreshTokenValue = null;
                if (result.tokens.refreshToken.value) {
                  refreshTokenValue = result.tokens.refreshToken.value;
                } else if (typeof result.tokens.refreshToken === "string") {
                  refreshTokenValue = result.tokens.refreshToken;
                } else if (result.tokens.refreshToken.refreshToken) {
                  refreshTokenValue = result.tokens.refreshToken.refreshToken;
                }

                if (refreshTokenValue) {
                  tokenObjects.refreshToken = {
                    refreshToken: refreshTokenValue,
                    expiresAt: result.tokens.refreshToken.expiresAt,
                  };
                }
              }

              if (Object.keys(tokenObjects).length > 0) {
                oktaAuth.tokenManager.setTokens(tokenObjects);
                logger.debug("Tokens loaded from dialog result (fallback)");
              }
            } catch (tokenError) {
              logger.error("Error loading tokens into tokenManager:", tokenError);
            }
          }
        }

        logger.debug("Tokens already saved by authDialog.js, skipping persistTokens()");

        if (!result.user) {
          throw new Error("User information not received from authentication");
        }

        return result.user;
      })
      .catch((error) => {
        logger.error("Error in loginWithPopup (Office dialog):", error);
        throw error;
      });
  }

  return loginWithWindowPopup(environment);
};

const loginWithWindowPopup = (environment) => {
  return new Promise((resolve, reject) => {
    // Get environment-specific config
    const envConfig = getOktaConfigForEnvironment(environment);

    // Create a temporary OktaAuth instance with the environment-specific config
    // We'll need to use this for the popup flow
    const tempOktaAuth = new OktaAuth({
      issuer: envConfig.issuer,
      clientId: envConfig.clientId,
      redirectUri: envConfig.redirectUri,
      scopes: envConfig.scopes,
      pkce: envConfig.pkce,
      responseType: ["code"],
      tokenManager: {
        storage: "memory",
        autoRenew: false,
        expireEarlySeconds: 30,
      },
    });
    let popup;

    try {
      popup = window.open(
        "",
        "Okta Login",
        "width=500,height=600,scrollbars=yes,resizable=yes,left=100,top=100"
      );
    } catch (e) {
      logger.error("Error opening popup:", e);
      reject(new Error("Failed to open popup window. Error: " + e.message));
      return;
    }

    if (!popup) {
      logger.error("Popup is null - likely blocked by browser or webview");
      reject(new Error("Popup blocked. Please allow popups for this site."));
      return;
    }

    try {
      popup.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Loading...</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                background: #f5f5f5;
              }
              .loader {
                text-align: center;
              }
              .spinner {
                border: 4px solid #f3f3f3;
                border-top: 4px solid #0078d4;
                border-radius: 50%;
                width: 40px;
                height: 40px;
                animation: spin 1s linear infinite;
                margin: 0 auto 20px;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
          </head>
          <body>
            <div class="loader">
              <div class="spinner"></div>
              <p>Preparing authentication...</p>
            </div>
          </body>
        </html>
      `);
      popup.document.close();
    } catch (e) {
      void e;
    }

    // Set redirectUri dynamically based on current window location
    let baseUrl = window.location.origin;
    if (baseUrl.includes("localhost:3000") && baseUrl.startsWith("http://")) {
      baseUrl = baseUrl.replace("http://", "https://");
    }
    const dynamicRedirectUri = baseUrl + "/login/callback";
    const finalConfig = {
      ...envConfig,
      redirectUri: dynamicRedirectUri,
    };

    tempOktaAuth.token
      .prepareTokenParams({
        responseType: ["code"],
        scopes: finalConfig.scopes,
        redirectUri: finalConfig.redirectUri,
      })
      .then((params) => {
        sessionStorage.setItem("okta-oauth-state", params.state);
        sessionStorage.setItem("okta-oauth-code-verifier", params.codeVerifier);

        const authParams = new URLSearchParams({
          client_id: finalConfig.clientId,
          response_type: "code",
          scope: finalConfig.scopes.join(" "),
          redirect_uri: finalConfig.redirectUri,
          state: params.state,
          code_challenge: params.codeChallenge,
          code_challenge_method: params.codeChallengeMethod,
        });

        const authorizationUrl = `${finalConfig.issuer}/v1/authorize?${authParams.toString()}`;

        try {
          if (popup.closed) {
            sessionStorage.removeItem("okta-oauth-state");
            sessionStorage.removeItem("okta-oauth-code-verifier");
            reject(new Error("Popup was closed before navigation"));
            return;
          }
        } catch (e) {
          void e;
        }

        try {
          popup.location.href = authorizationUrl;
        } catch (e) {
          void e;
          try {
            popup.location.replace(authorizationUrl);
          } catch (e2) {
            void e2;
            try {
              popup.location = authorizationUrl;
            } catch (e3) {
              void e3;
              popup.close();
              sessionStorage.removeItem("okta-oauth-state");
              sessionStorage.removeItem("okta-oauth-code-verifier");
              reject(
                new Error(
                  "Could not navigate popup to authorization URL. This may be a browser security restriction."
                )
              );
              return;
            }
          }
        }

        let messageListener;
        let checkClosed;

        messageListener = async (event) => {
          const allowedOrigins = [
            window.location.origin,
            "http://localhost:3000",
            "https://localhost:3000",
          ];

          if (!allowedOrigins.includes(event.origin)) {
            return;
          }

          if (event.data.type === "OKTA_AUTH_SUCCESS") {
            window.removeEventListener("message", messageListener);
            clearInterval(checkClosed);

            try {
              const { code, state } = event.data;

              const storedState = sessionStorage.getItem("okta-oauth-state");
              if (state !== storedState) {
                popup.close();
                sessionStorage.removeItem("okta-oauth-state");
                sessionStorage.removeItem("okta-oauth-code-verifier");
                reject(new Error("Authentication failed: invalid session. Please try again."));
                return;
              }

              const codeVerifier = sessionStorage.getItem("okta-oauth-code-verifier");

              const tokens = await tempOktaAuth.token.exchangeCodeForTokens({
                authorizationCode: code,
                redirectUri: finalConfig.redirectUri,
                codeVerifier: codeVerifier,
              });

              tempOktaAuth.tokenManager.setTokens(tokens.tokens);

              // Also update the main oktaAuth instance for compatibility
              oktaAuth.tokenManager.setTokens(tokens.tokens);

              await persistTokens({
                accessToken: tokens.tokens.accessToken,
                idToken: tokens.tokens.idToken,
                refreshToken: tokens.tokens.refreshToken,
                expiresAt: tokens.tokens.accessToken?.expiresAt || tokens.tokens.idToken?.expiresAt,
              });

              sessionStorage.removeItem("okta-oauth-state");
              sessionStorage.removeItem("okta-oauth-code-verifier");

              popup.close();

              const user = await tempOktaAuth.getUser();

              resolve(user);
            } catch (error) {
              logger.error("Error exchanging code for tokens:", error);
              popup.close();
              sessionStorage.removeItem("okta-oauth-state");
              sessionStorage.removeItem("okta-oauth-code-verifier");
              reject(error);
            }
          } else if (event.data.type === "OKTA_AUTH_ERROR") {
            window.removeEventListener("message", messageListener);
            clearInterval(checkClosed);
            popup.close();
            sessionStorage.removeItem("okta-oauth-state");
            sessionStorage.removeItem("okta-oauth-code-verifier");
            reject(new Error(event.data.error || "Authentication failed"));
          }
        };

        window.addEventListener("message", messageListener);

        checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener("message", messageListener);
            sessionStorage.removeItem("okta-oauth-state");
            sessionStorage.removeItem("okta-oauth-code-verifier");
            reject(new Error("Login popup was closed"));
          }
        }, 1000);
      })
      .catch((error) => {
        logger.error("Error preparing token params:", error);
        reject(error);
      });
  });
};

export async function persistUserInfo(user) {
  return tokenManager.persistUserInfo(user);
}

export async function restoreUserInfo() {
  return tokenManager.restoreUserInfo();
}

const getIdToken = () => {
  try {
    const idToken = oktaAuth.tokenManager.get("idToken");
    if (idToken && idToken.value) {
      return idToken.value;
    }
  } catch (error) {
    logger.warn("Could not get idToken:", error);
  }
  return null;
};

export const logout = async () => {
  try {
    logger.info("Starting logout process");

    const idTokenValue = getIdToken();

    // Clear tokenManager and transactionManager FIRST
    try {
      oktaAuth.tokenManager.clear();
      logger.debug("TokenManager cleared");
    } catch (tokenManagerError) {
      logger.warn("Error clearing tokenManager:", tokenManagerError);
    }

    try {
      oktaAuth.transactionManager?.clear();
      logger.debug("TransactionManager cleared");
    } catch (transactionError) {
      logger.warn("Error clearing transactionManager:", transactionError);
    }

    // Then clear cookies and storage
    await clearAllCookies();
    await clearStoredTokens();

    try {
      const { clearOrganizationsStorage } = await import("../services/organizationService");
      await clearOrganizationsStorage();
      logger.debug("Organizations storage cleared");
    } catch (orgStorageError) {
      logger.warn("Error clearing organizations storage on logout:", orgStorageError);
    }

    try {
      const postLogoutRedirectUri = `${window.location.origin}/taskpane.html`;
      await oktaAuth.signOut({ postLogoutRedirectUri });
      logger.debug("Okta signOut called");
    } catch (signOutError) {
      logger.warn("Error during oktaAuth.signOut():", signOutError);
    }

    if (idTokenValue) {
      try {
        const issuer = oktaAuth.options.issuer;
        if (issuer) {
          const postLogoutRedirectUri = `${window.location.origin}/taskpane.html`;
          const logoutUrl = `${issuer}/v1/logout?id_token_hint=${idTokenValue}&post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;

          const iframe = document.createElement("iframe");
          iframe.style.display = "none";
          iframe.src = logoutUrl;
          document.body.appendChild(iframe);

          await fetch(logoutUrl, {
            method: "GET",
            mode: "no-cors",
            credentials: "include",
            cache: "no-store",
          }).catch(() => {});

          setTimeout(() => {
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, 2000);

          logger.debug("Okta logout endpoint called");
        }
      } catch (logoutEndpointError) {
        logger.warn("Error calling Okta logout endpoint:", logoutEndpointError);
      }
    }

    try {
      if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
        await OfficeRuntime.storage.setItem("forceLoginPrompt", "true");
        logger.debug("forceLoginPrompt flag set");
      } else {
        sessionStorage.setItem("forceLoginPrompt", "true");
        logger.debug("forceLoginPrompt flag set");
      }
    } catch (flagError) {
      logger.warn("Error setting force login flag:", flagError);
    }

    try {
      if (typeof Office !== "undefined" && Office.context && Office.context.ui) {
        Office.context.ui.displayDialogAsync(
          "about:blank",
          {
            height: 10,
            width: 10,
          },
          (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              result.value.close();
            }
          }
        );
        logger.debug("Office dialog close attempted");
      }
    } catch (dialogError) {
      logger.warn("Error closing Office dialog:", dialogError);
    }

    logger.info("Logout process finished");
  } catch (error) {
    logger.error("Logout error:", error);
    throw error;
  }
};

export const loadTokensFromStorage = async () => {
  try {
    let tokensJson = await getSensitiveItem("authTokens");
    let source = tokensJson ? "sensitive:authTokens" : "none";
    if (!tokensJson) {
      tokensJson = await getSensitiveItem("oktaTokens");
      if (tokensJson) source = "sensitive:oktaTokens";
    }

    const runtimeAvailable = typeof OfficeRuntime !== "undefined" && !!OfficeRuntime?.storage;
    logger.info(
      "[DIAG:TP:loadTokensFromStorage] OfficeRuntime.storage available=%s",
      runtimeAvailable
    );

    const jsonSize = tokensJson ? tokensJson.length : 0;
    logger.info(
      "[DIAG:TP:loadTokensFromStorage] source=%s, found=%s, size=%s",
      source,
      !!tokensJson,
      jsonSize
    );

    if (tokensJson) {
      const tokens = JSON.parse(tokensJson);
      const tokenObjects = {};

      const topKeys = Object.keys(tokens).join(",");
      const atType = tokens.accessToken ? typeof tokens.accessToken : "missing";
      const atShape =
        tokens.accessToken && typeof tokens.accessToken === "object"
          ? Object.keys(tokens.accessToken).join(",")
          : atType;
      logger.info(
        "[DIAG:TP:loadTokensFromStorage] Parsed: topKeys=[%s], accessTokenType=%s, accessTokenShape=[%s]",
        topKeys,
        atType,
        atShape
      );

      if (tokens.accessToken) {
        if (tokens.accessToken.value) {
          tokenObjects.accessToken = {
            accessToken: tokens.accessToken.value,
            expiresAt: tokens.accessToken.expiresAt,
            tokenType: tokens.accessToken.tokenType || "Bearer",
            scopes: tokens.accessToken.scopes || [],
          };
          logger.info("[DIAG:TP:loadTokensFromStorage] accessToken extracted via .value path");
        } else if (typeof tokens.accessToken === "string") {
          tokenObjects.accessToken = {
            accessToken: tokens.accessToken,
            expiresAt: tokens.expiresAt || Math.floor(Date.now() / 1000) + 3600,
            tokenType: "Bearer",
            scopes: [],
          };
          logger.info("[DIAG:TP:loadTokensFromStorage] accessToken extracted as raw string");
        } else if (tokens.accessToken.accessToken) {
          tokenObjects.accessToken = {
            accessToken: tokens.accessToken.accessToken,
            expiresAt: tokens.accessToken.expiresAt,
            tokenType: tokens.accessToken.tokenType || "Bearer",
            scopes: tokens.accessToken.scopes || [],
          };
          logger.info(
            "[DIAG:TP:loadTokensFromStorage] accessToken extracted via .accessToken path"
          );
        } else {
          logger.debug(
            "[DIAG:TP:loadTokensFromStorage] accessToken present but could not extract value, shape: [%s]",
            atShape
          );
        }
      }

      if (tokens.idToken) {
        if (tokens.idToken.value) {
          tokenObjects.idToken = {
            idToken: tokens.idToken.value,
            expiresAt: tokens.idToken.expiresAt,
            claims: tokens.idToken.claims || {},
          };
        } else if (typeof tokens.idToken === "string") {
          tokenObjects.idToken = {
            idToken: tokens.idToken,
            expiresAt: tokens.expiresAt || Math.floor(Date.now() / 1000) + 3600,
            claims: {},
          };
        } else if (tokens.idToken.idToken) {
          tokenObjects.idToken = {
            idToken: tokens.idToken.idToken,
            expiresAt: tokens.idToken.expiresAt,
            claims: tokens.idToken.claims || {},
          };
        }
      }

      if (tokens.refreshToken) {
        if (tokens.refreshToken.value) {
          tokenObjects.refreshToken = {
            refreshToken: tokens.refreshToken.value,
            expiresAt: tokens.refreshToken.expiresAt,
          };
        } else if (typeof tokens.refreshToken === "string") {
          tokenObjects.refreshToken = {
            refreshToken: tokens.refreshToken,
            expiresAt: tokens.expiresAt || Math.floor(Date.now() / 1000) + 7200,
          };
        } else if (tokens.refreshToken.refreshToken) {
          tokenObjects.refreshToken = {
            refreshToken: tokens.refreshToken.refreshToken,
            expiresAt: tokens.refreshToken.expiresAt,
          };
        }
      }

      const loadedKeys = Object.keys(tokenObjects).join(",");
      logger.info("[DIAG:TP:loadTokensFromStorage] Loaded token types: [%s]", loadedKeys);

      if (Object.keys(tokenObjects).length > 0) {
        oktaAuth.tokenManager.setTokens(tokenObjects);
        logger.info(
          "[DIAG:TP:loadTokensFromStorage] Tokens set into oktaAuth.tokenManager successfully"
        );
        return true;
      }
    }

    logger.debug("[DIAG:TP:loadTokensFromStorage] No tokens loaded");
    return false;
  } catch (error) {
    logger.error("Error loading tokens from storage:", error);
    return false;
  }
};

export const isAuthenticated = async () => {
  try {
    let accessToken = null;
    try {
      accessToken = await oktaAuth.getAccessToken();
    } catch (tokenError) {
      void tokenError;
    }

    if (!accessToken) {
      const loaded = await loadTokensFromStorage();
      if (loaded) {
        try {
          accessToken = await oktaAuth.getAccessToken();
        } catch (loadError) {
          logger.warn("Error getting access token after loading from storage:", loadError);
        }
      }
    }

    if (!accessToken) {
      try {
        const tokens = oktaAuth.tokenManager.getTokensSync();
        if (tokens && (tokens.accessToken || tokens.idToken)) {
          return true;
        }
      } catch (syncError) {
        void syncError;
      }
    }

    return !!accessToken;
  } catch (error) {
    logger.error("Error in isAuthenticated:", error);
    return false;
  }
};

export const getUser = async () => {
  try {
    const tokensLoaded = await loadTokensFromStorage();

    let hasTokens = false;
    try {
      const tokens = oktaAuth.tokenManager.getTokensSync();
      hasTokens = !!(tokens && (tokens.accessToken || tokens.idToken));
    } catch (e) {
      void e;
    }

    if (!hasTokens && !tokensLoaded) {
      const restoredTokens = await restoreTokens();
      if (restoredTokens) {
        await loadTokensFromStorage();
      }
    }

    const isAuth = await isAuthenticated();
    if (!isAuth) {
      logger.warn("Not authenticated, cannot get user");
      return null;
    }

    if (!oktaAuth || !oktaAuth.options || !oktaAuth.options.issuer) {
      logger.warn("oktaAuth not properly configured, using idToken fallback");
      try {
        const idToken = oktaAuth.tokenManager.get("idToken");
        if (idToken && idToken.claims) {
          logger.info("Using idToken claims as user info (oktaAuth not configured)");
          return {
            sub: idToken.claims.sub,
            email: idToken.claims.email,
            name: idToken.claims.name || idToken.claims.preferred_username,
            ...idToken.claims,
          };
        }
      } catch (idTokenError) {
        logger.error("Error extracting user from idToken:", idTokenError);
      }
      return null;
    }

    try {
      const accessToken = await oktaAuth.getAccessToken();
      if (!accessToken) {
        logger.warn("No access token available, using idToken fallback");
        const idToken = oktaAuth.tokenManager.get("idToken");
        if (idToken && idToken.claims) {
          logger.info("Using idToken claims as user info (no access token)");
          return {
            sub: idToken.claims.sub,
            email: idToken.claims.email,
            name: idToken.claims.name || idToken.claims.preferred_username,
            ...idToken.claims,
          };
        }
        return null;
      }

      const issuer = oktaAuth.options.issuer;
      if (!issuer) {
        throw new Error("Issuer is not configured");
      }

      const userinfoUrl = `${issuer}/v1/userinfo`;
      logger.debug("Fetching user info from userinfo endpoint");

      const response = await fetch(userinfoUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Userinfo request failed: ${response.status} ${response.statusText}`);
      }

      const user = await response.json();
      logger.debug("User info retrieved from userinfo endpoint");
      return user;
    } catch (getUserError) {
      logger.error("Error fetching user info:", getUserError);

      if (getUserError.message && getUserError.message.includes("undefined")) {
        logger.warn("Userinfo fetch failed due to undefined URL, using idToken fallback");
      }

      try {
        const idToken = oktaAuth.tokenManager.get("idToken");
        if (idToken && idToken.claims) {
          logger.info("Using idToken claims as user info (userinfo fetch failed)");
          return {
            sub: idToken.claims.sub,
            email: idToken.claims.email,
            name: idToken.claims.name || idToken.claims.preferred_username,
            ...idToken.claims,
          };
        }
      } catch (idTokenError) {
        logger.error("Error extracting user from idToken:", idTokenError);
      }
      return null;
    }
  } catch (error) {
    logger.error("Error getting user:", error);
    return null;
  }
};

export const getAccessToken = async () => {
  try {
    logger.info("[DIAG:TP:getAccessToken] Attempting oktaAuth.getAccessToken()");
    let accessToken = await oktaAuth.getAccessToken();
    logger.info(
      "[DIAG:TP:getAccessToken] oktaAuth.getAccessToken() returned: hasToken=%s",
      !!accessToken
    );

    if (!accessToken) {
      logger.info("[DIAG:TP:getAccessToken] No in-memory token, trying loadTokensFromStorage()");
      const loaded = await loadTokensFromStorage();
      logger.info("[DIAG:TP:getAccessToken] loadTokensFromStorage() result=%s", loaded);
      if (loaded) {
        accessToken = await oktaAuth.getAccessToken();
        logger.info(
          "[DIAG:TP:getAccessToken] After storage load, oktaAuth.getAccessToken() returned: hasToken=%s",
          !!accessToken
        );
      }
    }

    return accessToken;
  } catch (error) {
    logger.error("[DIAG:TP:getAccessToken] Exception: %s", error.message);
    return null;
  }
};
